import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, Circle, Eye, PaperPlaneTilt, LinkSimple, UserPlus, Trash, Envelope, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const STATUS_STYLES = {
  new: 'bg-slate-100 text-slate-700',
  invited: 'bg-sky-100 text-sky-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  hired: 'bg-violet-100 text-violet-800',
};
export const STATUS_LABELS = { new: 'Not Sent', invited: 'Invited', in_progress: 'In Progress', completed: 'Forms Complete', hired: 'Hired' };

const ApplicantDetailDialog = ({ applicantId, open, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [payRate, setPayRate] = useState('');
  const [hired, setHired] = useState(null);

  const load = async () => {
    try { const res = await onboardingAPI.getApplicant(applicantId); setA(res.data); }
    catch { toast.error('Failed to load applicant'); onClose(); }
  };
  useEffect(() => { if (open && applicantId) { setA(null); setHired(null); load(); } }, [open, applicantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewPdf = async (templateId) => {
    try {
      const res = await onboardingAPI.signedPdf(applicantId, templateId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Could not open PDF'); }
  };

  const viewDoc = async (docTypeId) => {
    try {
      const res = await onboardingAPI.applicantDocument(applicantId, docTypeId);
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Could not open document'); }
  };

  const resend = async () => {
    setBusy(true);
    try { await onboardingAPI.sendEmail(applicantId); toast.success(`Welcome email sent to ${a.email}`); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(a.onboarding_link); toast.success('Onboarding link copied'); }
    catch { toast.error('Copy failed'); }
  };

  const hire = async () => {
    setBusy(true);
    try {
      const res = await onboardingAPI.hire(applicantId, { pay_rate: payRate ? Number(payRate) : null });
      setHired(res.data); setHireOpen(false); toast.success('Caregiver profile created'); await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hire failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${a.first_name} ${a.last_name} from onboarding?`)) return;
    try { await onboardingAPI.deleteApplicant(applicantId); toast.success('Applicant removed'); onChanged?.(); onClose(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="applicant-detail-dialog">
        {!a ? <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div> : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-6">
                <div>
                  <DialogTitle className="font-heading text-xl">{a.first_name} {a.last_name}</DialogTitle>
                  <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Envelope className="w-3.5 h-3.5" />{a.email}</a>
                    {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="w-3.5 h-3.5" />{a.phone}</a>}
                  </DialogDescription>
                </div>
                <Badge className={`rounded-full ${STATUS_STYLES[a.status]}`} data-testid="applicant-status-badge">{STATUS_LABELS[a.status]}</Badge>
              </div>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-3 text-center text-xs mt-2">
              <div className="rounded-xl bg-muted/50 p-3"><p className="text-muted-foreground">Invited</p><p className="font-medium mt-0.5">{a.invited_at ? format(new Date(a.invited_at), 'MMM d, yyyy') : '—'}</p></div>
              <div className="rounded-xl bg-muted/50 p-3"><p className="text-muted-foreground">Forms · Docs</p><p className="font-medium mt-0.5 tabular-nums" data-testid="applicant-forms-progress">{a.completed_forms} / {a.total_forms}{a.required_docs ? ` · ${a.completed_required_docs} / ${a.required_docs}` : ''}</p></div>
              <div className="rounded-xl bg-muted/50 p-3"><p className="text-muted-foreground">Last email</p><p className="font-medium mt-0.5">{a.last_email_at ? format(new Date(a.last_email_at), 'MMM d, h:mm a') : '—'}</p></div>
            </div>

            {hired && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-1" data-testid="hire-result">
                <p className="font-medium text-emerald-900">Caregiver login created</p>
                <p>Email: <span className="font-mono">{hired.login_email}</span></p>
                <p>Temporary password: <span className="font-mono font-semibold" data-testid="temp-password">{hired.temp_password}</span> <span className="text-emerald-800">(they'll be asked to change it on first login)</span></p>
              </div>
            )}

            <div className="space-y-2 mt-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Onboarding forms</Label>
              {a.form_list.length === 0 && <p className="text-sm text-muted-foreground">No forms in the packet yet — add them under Packet Forms.</p>}
              {a.form_list.map(f => (
                <div key={f.template_id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`applicant-form-${f.template_id}`}>
                  {f.status === 'completed' ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600 shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground/50 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground">{f.status === 'completed' ? `Signed ${format(new Date(f.submitted_at), 'MMM d, yyyy h:mm a')}` : 'Waiting on applicant'}</p>
                  </div>
                  {f.status === 'completed' && <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => viewPdf(f.template_id)} data-testid={`view-signed-pdf-${f.template_id}`}><Eye className="w-3.5 h-3.5" /> PDF</Button>}
                </div>
              ))}
            </div>

            {a.document_list?.length > 0 && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Documents</Label>
                {a.document_list.map(d => (
                  <div key={d.doc_type_id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`applicant-doc-${d.doc_type_id}`}>
                    {d.status === 'uploaded' ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600 shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground/50 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.name} {!d.required && <span className="text-xs text-muted-foreground">(optional)</span>}</p>
                      <p className="text-xs text-muted-foreground truncate">{d.status === 'uploaded' ? `Uploaded ${format(new Date(d.uploaded_at), 'MMM d, yyyy h:mm a')} · ${d.filename}` : 'Not uploaded yet'}</p>
                    </div>
                    {d.status === 'uploaded' && <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => viewDoc(d.doc_type_id)} data-testid={`view-doc-${d.doc_type_id}`}><Eye className="w-3.5 h-3.5" /> View</Button>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-3 border-t border-border mt-2">
              <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={resend} disabled={busy} data-testid="resend-email-btn"><PaperPlaneTilt className="w-4 h-4" /> {a.invited_at ? 'Resend Email' : 'Send Welcome Email'}</Button>
              <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={copyLink} data-testid="copy-link-btn"><LinkSimple className="w-4 h-4" /> Copy Link</Button>
              {a.status === 'hired' ? (
                <Button size="sm" className="rounded-xl gap-1 ml-auto" onClick={() => navigate(`/caregivers/${a.hired_caregiver_id}`)} data-testid="open-caregiver-btn">Open Caregiver Profile</Button>
              ) : (
                <Button size="sm" className="rounded-xl gap-1 ml-auto bg-emerald-600 hover:bg-emerald-700" onClick={() => setHireOpen(true)} disabled={busy} data-testid="hire-btn"><UserPlus weight="bold" className="w-4 h-4" /> Hire → Convert to Caregiver</Button>
              )}
              {a.status !== 'hired' && <Button variant="ghost" size="sm" className="rounded-xl text-destructive h-9 w-9 p-0" onClick={remove} data-testid="delete-applicant-btn"><Trash className="w-4 h-4" /></Button>}
            </div>

            <Dialog open={hireOpen} onOpenChange={setHireOpen}>
              <DialogContent className="sm:max-w-md" data-testid="hire-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">Hire {a.first_name} {a.last_name}?</DialogTitle>
                  <DialogDescription>This creates an active caregiver profile and a login ({a.email}) with a temporary password. Signed onboarding PDFs are attached to their Files tab.{a.status !== 'completed' && ` Note: only ${a.completed_forms} of ${a.total_forms} forms are complete.`}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <Label>Hourly pay rate (optional)</Label>
                  <Input type="number" step="0.01" min="0" value={payRate} onChange={(e) => setPayRate(e.target.value)} placeholder="e.g. 18.50" className="rounded-xl" data-testid="hire-pay-rate-input" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setHireOpen(false)}>Cancel</Button>
                  <Button className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={hire} disabled={busy} data-testid="hire-confirm-btn">{busy ? 'Creating…' : 'Confirm Hire'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicantDetailDialog;
